import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';

import { formFieldInStack } from './formFieldStackContext';
import { FormRadioOptionPressable } from './FormRadioOptionPressable';
import {
  ViewOnlyFieldValue,
  resolveIsViewOnlyPresentation,
  resolveYesNoViewOnlyDisplayText,
} from './viewOnlyFieldUtils';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight, lineHeight } from '../../theme/typography';
import { AlertCircle } from '../icons';

export interface FormYesNoChoiceProps {
  label: string;
  value: string;
  onSelect: (value: 'Yes' | 'No') => void;
  required?: boolean;
  disabled?: boolean;
  /** When false with `disabled`, keeps legacy read-only chrome instead of view-only text. */
  editable?: boolean;
  /** View-only request mode: plain text / dash instead of radio controls. */
  readOnlyMode?: boolean;
  error?: string;
  helperText?: string;
  containerStyle?: object;
  /** E.g. for integration tests: `form-yes-no-oppSiteAssessmentRequired` */
  testID?: string;
  /**
   * When `true`, label and Yes/No options share one row (saves vertical space; wraps on narrow viewports).
   * Default: stacked (label on first line, radios on second) — use for most forms.
   */
  inline?: boolean;
  /**
   * When `true`, no visible label is rendered (e.g. parent uses `FormFieldLabelWithInfo`).
   * Keep `label` for `accessibilityLabel` on the radio group.
   */
  hideLabel?: boolean;
}

const OPTIONS: readonly ('Yes' | 'No')[] = ['Yes', 'No'];

/** Normalize visible yes/no prompt to end with a single `?` (no stray spaces before it). */
export function yesNoQuestionLabel(label: string): string {
  const t = label.trim().replace(/\?+$/, '').trim();
  if (!t) return '';
  return `${t}?`;
}

/**
 * Two-option “Yes / No” control (radio group semantics). Use instead of a dropdown
 * for simple binary required fields. Values are the strings `Yes` and `No` (API / DB).
 */
export const FormYesNoChoice: React.FC<FormYesNoChoiceProps> = ({
  label,
  value,
  onSelect,
  required = false,
  disabled = false,
  editable = true,
  readOnlyMode = false,
  error,
  helperText,
  containerStyle,
  testID,
  inline = true,
  hideLabel = false,
}) => {
  const { canEditRequest } = useFormPermissions();
  const displayLabel = yesNoQuestionLabel(label);
  const isPermissionLocked = !disabled && !readOnlyMode && !canEditRequest;
  const isControlDisabled = disabled || readOnlyMode || isPermissionLocked;
  const isViewOnlyPresentation = resolveIsViewOnlyPresentation({
    readOnlyMode,
    disabled,
    editable,
    canEditRequest,
  });

  if (isViewOnlyPresentation) {
    return (
      <ViewOnlyFieldValue
        label={displayLabel || label}
        displayText={resolveYesNoViewOnlyDisplayText(value)}
        hideLabel={hideLabel}
        testID={testID}
        valueTestID={testID ? `${testID}-value` : 'view-only-field-value'}
        containerStyle={[containerStyle, formFieldInStack]}
        error={error}
        helperText={helperText}
      />
    );
  }

  const radios = OPTIONS.map(opt => {
    const isSelected = value === opt;
    return (
      <FormRadioOptionPressable
        key={opt}
        optionLabel={opt}
        selected={isSelected}
        disabled={isControlDisabled}
        onPress={() => onSelect(opt)}
        testID={testID ? `${testID}-${opt}` : undefined}
        pairWithLegendInline={inline}
      />
    );
  });

  const footer = (
    <>
      {error ? (
        <View style={styles.errorRow}>
          <AlertCircle
            size={14}
            color={colors.error}
            style={styles.errorRowIcon}
          />
          <Text style={styles.errorText} accessibilityRole="text">
            {error}
          </Text>
        </View>
      ) : null}
      {helperText && !error ? (
        <Text style={styles.helperText} accessibilityRole="text">
          {helperText}
        </Text>
      ) : null}
    </>
  );

  const radioA11yLabel = hideLabel
    ? displayLabel + (required ? ' required' : '')
    : undefined;

  if (inline) {
    return (
      <View
        style={[
          styles.formGroup,
          styles.formGroupInline,
          hideLabel && styles.formGroupBesideExternalLegend,
          containerStyle,
          formFieldInStack,
        ]}
        testID={testID}
      >
        {hideLabel ? (
          <View
            style={styles.radiosRowInline}
            accessibilityRole="radiogroup"
            accessibilityLabel={radioA11yLabel}
          >
            {radios}
          </View>
        ) : (
          <View style={styles.inlineAllRow}>
            <Text
              style={styles.labelInline}
              accessible={false}
              accessibilityElementsHidden
              importantForAccessibility="no"
            >
              {displayLabel}
              {required ? <Text style={styles.required}> *</Text> : null}
            </Text>
            <View
              style={styles.radiosRowInline}
              accessibilityRole="radiogroup"
              accessibilityLabel={displayLabel + (required ? ' required' : '')}
            >
              {radios}
            </View>
          </View>
        )}
        {footer}
      </View>
    );
  }

  return (
    <View
      style={[styles.formGroup, containerStyle, formFieldInStack]}
      testID={testID}
    >
      {!hideLabel ? (
        <View style={styles.labelRow}>
          <Text style={styles.label}>
            {displayLabel}
            {required ? <Text style={styles.required}> *</Text> : null}
          </Text>
        </View>
      ) : null}
      <View
        style={styles.radiosRow}
        accessibilityRole="radiogroup"
        accessibilityLabel={hideLabel ? radioA11yLabel : displayLabel}
      >
        {radios}
      </View>
      {footer}
    </View>
  );
};

const styles = StyleSheet.create({
  /** Vertical rhythm between fields comes from {@link FormFieldStack} `gap` / card padding, not here. */
  formGroup: {
    marginBottom: 0,
  },
  formGroupInline: {
    marginBottom: 0,
  },
  /** @see FormTwoOptionChoice `formGroupBesideExternalLegend` */
  formGroupBesideExternalLegend: {
    alignSelf: 'flex-start',
    flexGrow: 0,
    flexShrink: 0,
    /** Default column `alignItems: stretch` widens the radio row to the full column — looks “end-aligned”. */
    alignItems: 'flex-start',
    marginBottom: 0,
    ...Platform.select({
      web: {
        width: 'auto' as const,
        maxWidth: '100%' as const,
      },
      default: {},
    }),
  },
  labelRow: {
    marginBottom: spacing.xs,
  },
  label: {
    fontSize: fontSize.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  required: {
    color: colors.error,
  },
  radiosRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: spacing.formRadioGroupColumnGap,
    rowGap: spacing.sm,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 4,
    gap: 6,
  },
  errorRowIcon: {
    marginTop: 1,
  },
  errorText: {
    fontSize: 12,
    color: colors.error,
    flex: 1,
  },
  helperText: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 4,
  },
  /** Label + yes/no on one line (UX: less scroll; still wraps on narrow viewports). */
  inlineAllRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    rowGap: 4,
    paddingVertical: 0,
  },
  labelInline: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
    marginRight: 8,
    flexGrow: 0,
    flexShrink: 1,
  },
  radiosRowInline: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: spacing.formRadioGroupColumnGap,
  },
});
